// frontend/src/components/ResultsTable.jsx
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getPerson } from "../api";

export default function ResultsTable({ data, onPage }) {
  const [openId, setOpenId] = useState(null);
  const [personCache, setPersonCache] = useState(new Map());
  const navigate = useNavigate();

  if (!data) {
    return null;
  }

  const results = data.results || [];

  function handleToggleRow(r) {
    const nextOpenId = openId === r.id ? null : r.id;
    setOpenId(nextOpenId);

    if (nextOpenId && r.type === "person" && !personCache.has(r.id)) {
      getPerson(r.id)
        .then((person) => {
          setPersonCache((prevCache) => {
            const newCache = new Map(prevCache);
            newCache.set(r.id, person);
            return newCache;
          });
        })
        .catch(() => {
          // Handle error silently
        });
    }
  }

  function handlePersonNavigation(personId) {
    navigate(`/person/${personId}`);
  }

  // ✅ FIXED: Better formatted details with proper spacing
  function renderDetails(result) {
    if (result.type === "person") {
      return (
        <div className="search-result-details">
          <div className="detail-line">
            Companies: <b>{result.companies || "—"}</b>
          </div>
        </div>
      );
    }

    if (result.type === "company") {
      return (
        <div className="search-result-details">
          <div className="detail-line">
            <span className="detail-item">
              Reg: <b>{result.registration_no || "—"}</b>
            </span>
            <span className="detail-separator">•</span>
            <span className="detail-item">
              Res: <b>{result.residency_status || "—"}</b>
            </span>
            <span className="detail-separator">•</span>
            <span className="detail-item">
              Class: <b>{result.type_local_foreign || "—"}</b>
            </span>
          </div>
          <div className="detail-line">
            Services: <b>{result.services || "—"}</b>
          </div>
          <div className="detail-line">
            Activity: <b>{result.business_activity || "—"}</b>
          </div>
        </div>
      );
    }

    return (
      <div className="search-result-details">
        <div className="detail-line">
          Companies: <b>{result.companies || "—"}</b>
        </div>
      </div>
    );
  }

  function renderName(result) {
    if (result.type === "person") {
      const displayName = [result.last_name, result.first_name].filter(Boolean).join(", ") || result.full_name || `Person #${result.id}`;
      return (
        <div style={{ fontWeight: 700 }}>
          <Link to={`/person/${result.id}`}>{displayName}</Link>
        </div>
      );
    }

    if (result.type === "company") {
      return (
        <>
          <div style={{ fontWeight: 700 }}>
            <Link to={`/company/${result.id}`}>{result.name || result.ic_name || `Company #${result.id}`}</Link>
          </div>
          {result.ic_name && result.ic_name !== result.name && (
            <div className="kv">
              IC: <b>{result.ic_name}</b>
            </div>
          )}
        </>
      );
    }

    return <div style={{ fontWeight: 700 }}>{result.title || result.name || result.full_name || `#${result.id}`}</div>;
  }

  function getTypeBadge(type) {
    if (type === "person") return "Person";
    if (type === "company") return "Company";
    return type;
  }

  return (
    <div className="results-wrapper">
      {results.length > 0 ? (
        <div className="card">
          <div className="table-container">
            <table className="table overview-compact link">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Type</th>
                  <th style={{ width: 32 }} />
                  <th>Name</th>
                  <th style={{ width: 520 }}>Details</th>
                  <th style={{ width: 160 }}>Country</th>
                </tr>
              </thead>

              {results.map((result) => {
                const isOpen = openId === result.id;
                const canExpand = result.type === "person";

                return (
                  <tbody key={`${result.type}-${result.id}`}>
                    <tr className={isOpen ? "row-open" : ""} onClick={() => canExpand && handleToggleRow(result)} style={{ cursor: canExpand ? "pointer" : "default" }}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <span className="badge">{getTypeBadge(result.type)}</span>
                      </td>
                      <td style={{ textAlign: "center" }}>{canExpand ? (isOpen ? "▾" : "▸") : ""}</td>
                      <td onClick={(e) => e.stopPropagation()}>{renderName(result)}</td>
                      <td onClick={(e) => e.stopPropagation()}>{renderDetails(result)}</td>
                      <td onClick={(e) => e.stopPropagation()}>{result.country || "—"}</td>
                    </tr>

                    {canExpand && (
                      <tr className="row-expand" hidden={!isOpen}>
                        <td colSpan={5}>
                          <div className="detail-grid">
                            {(() => {
                              const person = personCache.get(result.id);
                              if (!person) {
                                return <div className="muted">Loading…</div>;
                              }

                              const fullName = person.full_name || [person.first_name, person.last_name].filter(Boolean).join(" ");

                              return (
                                <>
                                  <div>
                                    <b>FULL NAME</b>
                                    <div>{fullName || `Person #${result.id}`}</div>
                                  </div>
                                  <div>
                                    <b>NATIONALITY</b>
                                    <div>{person.nationality || "—"}</div>
                                  </div>
                                  <div>
                                    <b>COUNTRY (ISO2)</b>
                                    <div>{person.country_iso2 || "—"}</div>
                                  </div>
                                  <div>
                                    <b>DATE OF BIRTH</b>
                                    <div>{person.date_of_birth?.slice(0, 10) || "—"}</div>
                                  </div>
                                  <div>
                                    <b>PLACE OF BIRTH</b>
                                    <div>{person.place_of_birth || "—"}</div>
                                  </div>
                                  <div>
                                    <b>PEP</b>
                                    <div>{person.pep || "—"}</div>
                                  </div>
                                  <div>
                                    <b>ID NUMBER</b>
                                    <div>{person.id_number || "—"}</div>
                                  </div>
                                  <div>
                                    <b>PASSPORT #</b>
                                    <div>{person.passport_number || "—"}</div>
                                  </div>
                                  <div>
                                    <b>TAX NUMBER</b>
                                    <div>{person.tax_number || "—"}</div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                          <div className="detail-actions">
                            <button
                              type="button"
                              className="btn primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePersonNavigation(result.id);
                              }}>
                              Open Person
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                );
              })}
            </table>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table overview-compact">
              <tbody>
                <tr>
                  <td colSpan={5} className="empty">
                    No results.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
